// The Twin — the digital twin's owner page (plan Phase 4).
//
// One sentence explains it: the orange line is your farm, the blue line is
// the farm if nothing had changed, the green line is where it can defensibly
// go. Everything else on the page exists to make that picture trustworthy:
// the plausible band around the best case, this week's checks against the
// farm's own trajectory, the assumptions ledger with a provenance badge on
// every number, and the feed of what reality taught the model.
//
// Server contracts: routes/twin.py (trajectories | assumptions | status).
// All reads are ranch-member visible; assumption WRITES are owner-gated
// server-side and not offered here (supersedes flow through review, not a
// casual form).

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Area, ComposedChart, Line, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts'
import {
  AlertTriangle, Banknote, BookOpenCheck, CheckCircle2, GitCompareArrows,
  History, Leaf, PlayCircle, Receipt, Scale, Sparkles, Stethoscope,
  TrendingUp,
} from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import { CustomTooltip, axisTick, chartMargin, gridProps } from '../../components/ChartConfig'
import {
  getTwinAssumptions, getTwinFeeStatement, getTwinStatus, getTwinTrajectories,
  getTwinUplift, getTwinValue, getWeeklyReport, getWeeklyReports, recomputeTwin,
} from '../../services/api'

// Semantic series colors (match the twin textbook's roles, tuned to the app):
const C = {
  base: '#64748b',    // the counterfactual — cool, receding
  actual: '#b45309',  // the measured farm — warm, emphasized
  best: '#15803d',    // the phased best case — growth green
  band: 'rgba(21, 128, 61, 0.12)',
}

const fmtCompactUGX = (v) => (Math.abs(v) >= 1e9
  ? `UGX ${(v / 1e9).toFixed(2)} bn` : `UGX ${(v / 1e6).toFixed(0)} M`)

const METRICS = [
  { key: 'herd_liveweight_kg', label: 'Liveweight', fmt: (v) => `${(v / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} t` },
  { key: 'herd_value_ugx', label: 'Herd value', fmt: fmtCompactUGX },
  { key: 'herd_head', label: 'Cattle count', fmt: (v) => `${Math.round(v).toLocaleString()} head` },
  { key: 'annual_sales_value_ugx', label: 'Yearly sales', fmt: fmtCompactUGX },
]

// Drill-down metrics: reachable by tapping a check tile, not from the toggle.
const DRILL_METRICS = [
  { key: 'breeding_cows', label: 'Breeding cows', fmt: (v) => `${Math.round(v).toLocaleString()} cows` },
  { key: 'annual_births', label: 'Calves per model year', fmt: (v) => `${Math.round(v).toLocaleString()} calves` },
  { key: 'annual_sales_head', label: 'Sales per model year', fmt: (v) => `${Math.round(v).toLocaleString()} sold` },
  { key: 'herd_lsu', label: 'Grazing pressure (LSU)', fmt: (v) => `${Math.round(v).toLocaleString()} LSU` },
  { key: 'annual_health_cost_ugx', label: 'Herd health spend per year', fmt: fmtCompactUGX },
]
const ALL_METRICS = [...METRICS, ...DRILL_METRICS]

// Tap a check tile -> the hero chart shows that driver's three lines.
const CHECK_METRIC = {
  herd_head: 'herd_head',
  breeding_cows: 'breeding_cows',
  calving_pace: 'annual_births',
  sales_pace: 'annual_sales_head',
}

const fmtUGX = (v) => {
  const sign = v < 0 ? '−' : ''
  const a = Math.abs(v)
  if (a >= 1e9) return `${sign}UGX ${(a / 1e9).toFixed(2)} bn`
  if (a >= 1e6) return `${sign}UGX ${(a / 1e6).toFixed(1)} M`
  return `${sign}UGX ${Math.round(a).toLocaleString()}`
}

const SERIES_LABELS = {
  base: 'If nothing had changed',
  actual: 'Your farm (measured)',
  best: 'Achievable (phased)',
}

const KEY_LABELS = {
  calving_rate: 'Calving rate',
  calf_mortality: 'Calf losses',
  young_mortality: 'Young-stock losses',
  cow_mortality: 'Cow losses',
  bull_mortality: 'Bull losses',
  weight_cow_kg: 'Mature cow weight',
  weight_bull_kg: 'Mature bull weight',
  weight_y1_kg: 'Yearling weight',
  weight_y2_kg: 'Two-year-old weight',
  weight_y3_kg: 'Three-year-old weight',
  cow_cull_rate: 'Cows replaced each year',
  heifer_retention: 'Heifers kept for the herd',
  bulls_per_100_cows: 'Bulls per 100 cows',
  price_per_kg_ugx: 'Liveweight price',
  rest_days_min: 'Minimum paddock rest',
  lsu_reference_mass_kg: 'Reference animal (LSU)',
  lsu_intake_kg_dm_day: 'Daily grass intake per LSU',
  forage_utilization_frac: 'Usable share of standing grass',
  grass_regrowth_cycles_per_year: 'Grass regrowth per year',
  pasture_biomass_multiplier: 'Pasture recovery (grass grown)',
  grazing_ha_per_lsu: 'Hectares per animal unit',
  acaricide_cost_per_head_ugx: 'Tick control per animal',
  vet_treatment_cost_per_head_ugx: 'Vet & treatment per animal',
  baseline_offtake_ugx_yr: 'Yearly sales before WildeBeef (signed baseline)',
  fee_wage_ugx_head_month: 'The Wage (watchman fee)',
  fee_reference_price_ugx_kg: 'Reference price (trader)',
  fee_premium_share_frac: "WildeBeef's premium share",
  fee_weaner_value_ugx: 'Calf-share settlement value',
}

const CHECK_LABELS = {
  herd_head: 'Cattle count',
  breeding_cows: 'Breeding cows',
  calving_pace: 'Calving pace',
  sales_pace: 'Sales pace',
}

const BASIS_STYLE = {
  measured: 'bg-green-100 text-green-800',
  verified: 'bg-green-100 text-green-800',
  derived: 'bg-teal-100 text-teal-800',
  benchmarked: 'bg-blue-100 text-blue-800',
  estimated: 'bg-amber-100 text-amber-800',
  assumed: 'bg-amber-100 text-amber-800',
  predicted: 'bg-gray-100 text-gray-700',
}

const BASIS_LABEL = {
  measured: 'Measured on this farm',
  verified: 'Independently verified',
  derived: 'Derived from records',
  benchmarked: 'Published benchmark',
  estimated: 'Estimated',
  assumed: 'Assumed — being tested',
  predicted: 'Model-predicted',
}

const monthLabel = (iso) => {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
}

function fmtAssumption(v, unit) {
  if (unit === 'fraction') return `${Math.round(v * 100)}%`
  if (unit === 'UGX/kg') return `UGX ${Math.round(v).toLocaleString()}/kg`
  if (unit === 'UGX/head/yr') return `UGX ${Math.round(v).toLocaleString()}/head`
  if (unit === 'UGX/head/mo') return `UGX ${Math.round(v).toLocaleString()}/head/mo`
  if (unit === 'UGX/calf') return `UGX ${Math.round(v).toLocaleString()}/calf`
  if (unit === 'UGX/yr') return `UGX ${(v / 1e6).toFixed(0)} M/yr`
  if (unit === 'kg') return `${Math.round(v)} kg`
  if (unit === 'days') return `${Math.round(v)} days`
  if (unit === 'x') return `${v > 1 ? '+' : ''}${Math.round((v - 1) * 100)}%`
  return `${v} ${unit}`
}

function BasisBadge({ basis }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${BASIS_STYLE[basis] || 'bg-gray-100 text-gray-700'}`}
          title={BASIS_LABEL[basis] || basis}>
      {BASIS_LABEL[basis] || basis}
    </span>
  )
}

function Card({ title, icon: Icon, children, right }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-green-700" />}
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        {right}
      </div>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------- hero chart

function TrajectoryChart({ traj, metric }) {
  const m = ALL_METRICS.find((x) => x.key === metric)
  const { rows, todayLabel } = useMemo(() => {
    const byMonth = new Map()
    for (const scenario of ['base', 'actual', 'best']) {
      for (const p of traj.scenarios[scenario] || []) {
        if (!byMonth.has(p.month)) byMonth.set(p.month, { month: p.month })
        const row = byMonth.get(p.month)
        row[scenario] = p.value
        if (scenario === 'best' && p.low != null && p.high != null) {
          row.bestBand = [p.low, p.high]
        }
      }
    }
    const rows = [...byMonth.values()]
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((r) => ({ ...r, label: monthLabel(r.month) }))
    const todayIso = new Date().toISOString().slice(0, 10)
    const past = rows.filter((r) => r.month <= todayIso)
    const todayLabel = (past.length ? past[past.length - 1] : rows[0])?.label
    return { rows, todayLabel }
  }, [traj])

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ ...chartMargin, right: 18 }}>
          <defs>
            <linearGradient id="twinActualFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.actual} stopOpacity={0.16} />
              <stop offset="100%" stopColor={C.actual} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...gridProps} vertical={false} />
          <XAxis dataKey="label" tick={axisTick} interval="preserveStartEnd"
                 minTickGap={40} />
          <YAxis tick={axisTick} width={64}
                 tickFormatter={(v) => m.fmt(v).replace('UGX ', '')}
                 domain={['auto', 'auto']} />
          <Tooltip content={<CustomTooltip formatter={(v, name) =>
            Array.isArray(v) ? `${m.fmt(v[0])} – ${m.fmt(v[1])}` : m.fmt(v)} />} />
          <Area dataKey="bestBand" name="Plausible range" stroke="none"
                fill={C.band} isAnimationActive={false} connectNulls />
          <Area dataKey="actual" stroke="none" fill="url(#twinActualFill)"
                legendType="none" tooltipType="none" activeDot={false}
                isAnimationActive={false} connectNulls />
          <Line dataKey="base" name={SERIES_LABELS.base} stroke={C.base}
                strokeWidth={2} strokeDasharray="6 5" dot={false}
                isAnimationActive={false} connectNulls />
          <Line dataKey="best" name={SERIES_LABELS.best} stroke={C.best}
                strokeWidth={2} strokeDasharray="6 5" dot={false}
                isAnimationActive={false} connectNulls />
          <Line dataKey="actual" name={SERIES_LABELS.actual} stroke={C.actual}
                strokeWidth={3} dot={false} isAnimationActive={false}
                connectNulls />
          {todayLabel && (
            <ReferenceLine x={todayLabel} stroke="#9a8a70" strokeDasharray="2 4"
                           label={{ value: 'Today', fontSize: 11, fill: '#9a8a70', position: 'top' }} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

// ---------------------------------------------------------------- main page

export default function TwinPage({ ranchId }) {
  const [metric, setMetric] = useState('herd_liveweight_kg')
  const queryClient = useQueryClient()

  const trajQ = useQuery({
    queryKey: ['twin-traj', ranchId, metric],
    queryFn: () => getTwinTrajectories(ranchId, metric),
    enabled: !!ranchId,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })
  // Liveweight + value always fetched for the gap tiles, whatever the toggle.
  const lwQ = useQuery({
    queryKey: ['twin-traj', ranchId, 'herd_liveweight_kg'],
    queryFn: () => getTwinTrajectories(ranchId, 'herd_liveweight_kg'),
    enabled: !!ranchId,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })
  const valQ = useQuery({
    queryKey: ['twin-traj', ranchId, 'herd_value_ugx'],
    queryFn: () => getTwinTrajectories(ranchId, 'herd_value_ugx'),
    enabled: !!ranchId,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })
  const statusQ = useQuery({
    queryKey: ['twin-status', ranchId],
    queryFn: () => getTwinStatus(ranchId),
    enabled: !!ranchId,
  })
  // Phase 5: grass vs herd (LSU + measured capacity) and the value waterfall.
  const lsuQ = useQuery({
    queryKey: ['twin-traj', ranchId, 'herd_lsu'],
    queryFn: () => getTwinTrajectories(ranchId, 'herd_lsu'),
    enabled: !!ranchId,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })
  const capQ = useQuery({
    queryKey: ['twin-traj', ranchId, 'grazing_capacity_lsu'],
    queryFn: () => getTwinTrajectories(ranchId, 'grazing_capacity_lsu'),
    enabled: !!ranchId,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })
  const valueQ = useQuery({
    queryKey: ['twin-value', ranchId],
    queryFn: () => getTwinValue(ranchId),
    enabled: !!ranchId,
  })
  // Quarterly realized uplift (cash events vs the counterfactual).
  // Owner-gated server-side; non-owners get finance_visible: false.
  const upliftQ = useQuery({
    queryKey: ['twin-uplift', ranchId],
    queryFn: () => getTwinUplift(ranchId),
    enabled: !!ranchId,
    retry: false,
  })
  // The three payments in farmer units (memo edition 3); owner-gated.
  const feeQ = useQuery({
    queryKey: ['twin-fee', ranchId],
    queryFn: () => getTwinFeeStatement(ranchId),
    enabled: !!ranchId,
    retry: false,
  })
  // Phase 6: "this week, in plain words" — the latest Tuesday Report's
  // narrative, verbatim (facts-only phrasing, owner's language, existing
  // guardrails). Digest endpoints are owner-gated: a 403 simply hides the
  // strip, so no retry.
  const digestListQ = useQuery({
    queryKey: ['twin-digest-list', ranchId],
    queryFn: () => getWeeklyReports(ranchId, { pageSize: 1 }),
    enabled: !!ranchId,
    retry: false,
  })
  const latestDigestId = digestListQ.data?.items?.[0]?.id
  const digestQ = useQuery({
    queryKey: ['twin-digest', latestDigestId],
    queryFn: () => getWeeklyReport(latestDigestId),
    enabled: !!latestDigestId,
    retry: false,
  })
  const assumptionsQ = useQuery({
    queryKey: ['twin-assumptions', ranchId],
    queryFn: () => getTwinAssumptions(ranchId, { includeSuperseded: true }),
    enabled: !!ranchId,
  })
  const recompute = useMutation({
    mutationFn: () => recomputeTwin(ranchId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['twin-traj'] }),
  })

  const noRunsYet = trajQ.error?.response?.status === 404

  // The "now" gaps, from the liveweight + value series.
  const gaps = useMemo(() => {
    const at = (q) => {
      const s = q.data?.scenarios
      if (!s?.base?.length || !s?.actual?.length || !s?.best?.length) return null
      const todayIso = new Date().toISOString().slice(0, 10)
      const idxOf = (arr) => {
        const past = arr.filter((p) => p.month <= todayIso)
        return past.length ? arr.indexOf(past[past.length - 1]) : 0
      }
      const i = idxOf(s.actual)
      return {
        created: s.actual[i].value - (s.base[i]?.value ?? s.actual[i].value),
        onTable: (s.best[i]?.value ?? s.actual[i].value) - s.actual[i].value,
      }
    }
    return { kg: at(lwQ), ugx: at(valQ) }
  }, [lwQ.data, valQ.data])

  const checks = statusQ.data?.checks || []
  const behind = checks.filter((c) => c.status === 'behind')
  const ahead = checks.filter((c) => c.status === 'ahead')

  // Grass vs herd, at the "now" index. Capacity series only exists once the
  // ranch has measured land data — the card hides itself honestly otherwise.
  const grass = useMemo(() => {
    const lsu = lsuQ.data?.scenarios
    const cap = capQ.data?.scenarios
    if (!lsu?.actual?.length || !cap?.actual?.length) return null
    const todayIso = new Date().toISOString().slice(0, 10)
    const nowOf = (arr) => {
      const past = arr.filter((p) => p.month <= todayIso)
      return past.length ? past[past.length - 1] : arr[0]
    }
    const demand = nowOf(lsu.actual).value
    const capacity = nowOf(cap.actual).value
    const bestEndLsu = lsu.best?.[lsu.best.length - 1]?.value
    const bestEndCap = cap.best?.[cap.best.length - 1]?.value
    return {
      demand,
      capacity,
      headroomPct: capacity > 0 ? ((capacity - demand) / capacity) * 100 : null,
      bestGrassLimited: bestEndLsu != null && bestEndCap != null
        && bestEndLsu >= bestEndCap * 0.98,
    }
  }, [lsuQ.data, capQ.data])

  const waterfall = valueQ.data?.available && valueQ.data?.finance_visible
    && valueQ.data?.gross_value_ugx != null ? valueQ.data : null

  const { liveByKey, history } = useMemo(() => {
    const rows = assumptionsQ.data?.assumptions || []
    const live = rows.filter((r) => !r.superseded_at)
    const liveByKey = new Map()
    for (const r of live) {
      if (!liveByKey.has(r.key)) liveByKey.set(r.key, [])
      liveByKey.get(r.key).push(r)
    }
    const history = rows.filter((r) => r.superseded_at)
      .sort((a, b) => b.superseded_at.localeCompare(a.superseded_at))
    return { liveByKey, history }
  }, [assumptionsQ.data])

  // Health-cost lever: per-head today vs the achievable plateau, straight
  // from the assumptions register (measured 2024 books vs the phased best).
  const healthLever = useMemo(() => {
    const per = (key, scenario) => {
      const rows = (liveByKey.get(key) || []).filter((r) =>
        scenario === 'base'
          ? (r.scenario === 'base' || r.scenario === 'shared')
          : r.scenario === 'best')
      if (!rows.length) return null
      const sorted = [...rows].sort((a, b) => a.effective_from.localeCompare(b.effective_from))
      return Number(scenario === 'base' ? sorted[0].value : sorted[sorted.length - 1].value)
    }
    const baseA = per('acaricide_cost_per_head_ugx', 'base')
    const baseV = per('vet_treatment_cost_per_head_ugx', 'base')
    const bestA = per('acaricide_cost_per_head_ugx', 'best')
    const bestV = per('vet_treatment_cost_per_head_ugx', 'best')
    if (baseA == null || baseV == null || bestA == null || bestV == null) return null
    const base = baseA + baseV
    const best = bestA + bestV
    return { base, best, baseA, pct: Math.round((1 - best / base) * 100) }
  }, [liveByKey])

  const uplift = upliftQ.data?.available && upliftQ.data?.finance_visible
    ? upliftQ.data : null
  const fee = feeQ.data?.available && feeQ.data?.finance_visible
    ? feeQ.data : null

  const fmtT = METRICS[0].fmt
  const fmtU = METRICS[1].fmt

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        icon={GitCompareArrows}
        title="The Twin"
        subtitle="The orange line is your farm. The blue line is the farm if nothing had changed. The green line is where it can defensibly go."
      />

      {noRunsYet ? (
        <Card title="The twin has not run yet" icon={Sparkles}>
          <p className="text-sm text-gray-600 mb-4">
            Once this farm's assumptions register is seeded, the twin projects
            three trajectories every week and compares them with what the farm
            actually records.
          </p>
          <button
            onClick={() => recompute.mutate()}
            disabled={recompute.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-700 text-white text-sm font-medium hover:bg-green-800 disabled:opacity-50"
          >
            <PlayCircle className="h-4 w-4" />
            {recompute.isPending ? 'Running…' : 'Run the twin now'}
          </button>
          {recompute.error && (
            <p className="text-sm text-amber-700 mt-3">
              {recompute.error.response?.data?.detail || 'The twin could not run — is the assumptions register seeded?'}
            </p>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {/* status strip */}
          {statusQ.data?.available && (
            <div className={`rounded-lg px-4 py-3 flex flex-wrap items-center gap-3 text-sm ${behind.length ? 'bg-amber-50 text-amber-900' : 'bg-green-50 text-green-900'}`}>
              {behind.length ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
              <span className="font-medium">
                {behind.length
                  ? `${behind.length} check(s) behind the farm's own trajectory`
                  : 'On trajectory — every check inside its expected range'}
              </span>
              {ahead.length > 0 && (
                <span className="text-green-800">
                  Ahead of forecast: {ahead.map((c) => CHECK_LABELS[c.metric] || c.metric).join(', ')} — a pleasant surprise, recorded.
                </span>
              )}
            </div>
          )}

          {/* hero chart */}
          <Card
            title="Three trajectories, one farm"
            icon={GitCompareArrows}
            right={
              <div className="flex gap-1 flex-wrap">
                {METRICS.map((m) => (
                  <button key={m.key} onClick={() => setMetric(m.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${metric === m.key ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {m.label}
                  </button>
                ))}
                {DRILL_METRICS.some((m) => m.key === metric) && (
                  <button onClick={() => setMetric('herd_liveweight_kg')}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-700 text-white"
                    title="Back to liveweight">
                    {ALL_METRICS.find((m) => m.key === metric).label} ×
                  </button>
                )}
              </div>
            }
          >
            {trajQ.isLoading && <p className="text-sm text-gray-500">Loading trajectories…</p>}
            {trajQ.data && <TrajectoryChart traj={trajQ.data} metric={metric} />}
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-600">
              <span className="flex items-center gap-1.5"><span className="w-5 border-t-2" style={{ borderColor: C.actual }} />{SERIES_LABELS.actual}</span>
              <span className="flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed" style={{ borderColor: C.base }} />{SERIES_LABELS.base}</span>
              <span className="flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed" style={{ borderColor: C.best }} />{SERIES_LABELS.best} (shaded = plausible range)</span>
            </div>
          </Card>

          {/* the two gaps — money first: owners think in shillings */}
          {gaps.kg && gaps.ugx && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg shadow p-5 bg-gradient-to-br from-green-50 to-white border border-green-100">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-green-800 font-semibold mb-1">
                  <TrendingUp className="h-3.5 w-3.5" /> Value created so far
                </div>
                <div className="text-3xl font-bold text-gray-900 tabular-nums">{fmtU(gaps.ugx.created)}</div>
                <div className="text-sm text-gray-600 mt-0.5">{fmtT(gaps.kg.created)} of liveweight vs if nothing had changed</div>
                <div className="text-xs text-gray-500 mt-1">an estimate above the modelled base case — the evidence an additionality case starts from</div>
              </div>
              <div className="rounded-lg shadow p-5 bg-gradient-to-br from-emerald-50 to-white border border-emerald-100">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-emerald-800 font-semibold mb-1">
                  <Sparkles className="h-3.5 w-3.5" /> Still on the table
                </div>
                <div className="text-3xl font-bold text-gray-900 tabular-nums">{fmtU(gaps.ugx.onTable)}</div>
                <div className="text-sm text-gray-600 mt-0.5">{fmtT(gaps.kg.onTable)} — the phased best case vs today</div>
                <div className="text-xs text-gray-500 mt-1">the to-do list, priced</div>
              </div>
            </div>
          )}

          {/* measured uplift — owner-only; the Pay-on-Proof fee base */}
          {uplift && (
            <Card title="Measured uplift, quarter by quarter" icon={Banknote}
              right={<span className="text-xs text-gray-400">the fee base — proof, not promises</span>}>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                <div>
                  <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold mr-2">Measured uplift to date</span>
                  <span className="text-2xl font-bold text-gray-900 tabular-nums">{fmtUGX(uplift.cumulative_uplift_ugx || 0)}</span>
                </div>
                {uplift.high_water_mark_ugx != null && (
                  <span className="text-xs text-gray-500">high-water mark {fmtUGX(uplift.high_water_mark_ugx)}</span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="text-left py-2 pr-3 font-semibold">Quarter</th>
                      <th className="text-right py-2 px-3 font-semibold">On the hoof (change)</th>
                      <th className="text-right py-2 px-3 font-semibold">Sales vs baseline</th>
                      <th className="text-right py-2 px-3 font-semibold">Health savings</th>
                      <th className="text-right py-2 px-3 font-semibold">New uplift</th>
                      <th className="text-right py-2 pl-3 font-semibold">20% fee (illustrative)</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {uplift.quarters.map((q) => (
                      <tr key={q.start} className="border-b border-gray-100">
                        <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{q.label}</td>
                        <td className={`py-2 px-3 text-right ${q.on_hoof_change_ugx > 0 ? 'text-green-700' : 'text-gray-600'}`}>
                          {q.on_hoof_change_ugx == null ? '—' : fmtUGX(q.on_hoof_change_ugx)}
                        </td>
                        <td className={`py-2 px-3 text-right ${q.sales.vs_baseline_ugx > 0 ? 'text-green-700' : 'text-gray-600'}`}>
                          {q.sales.vs_baseline_ugx == null ? '—' : fmtUGX(q.sales.vs_baseline_ugx)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600"
                            title={q.health_costs.note || undefined}>
                          {q.health_costs.savings_ugx == null ? '—' : fmtUGX(q.health_costs.savings_ugx)}
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-gray-900">
                          {q.new_uplift_ugx == null ? '—' : fmtUGX(q.new_uplift_ugx)}
                        </td>
                        <td className="py-2 pl-3 text-right font-bold text-green-800">
                          {q.illustrative_fee_ugx == null ? '—' : fmtUGX(q.illustrative_fee_ugx)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {uplift.on_the_hoof?.gap_value_ugx != null && (
                <p className="text-xs text-gray-500 mt-3">
                  On the hoof right now:{' '}
                  <b className="text-gray-700">{fmtUGX(uplift.on_the_hoof.gap_value_ugx)}</b>
                  {' '}— measured weekly at the kraal, valued at the measured
                  price, charged as it is measured and never twice: a sale
                  moves value from the hoof column to the sales column.
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                The fee applies only to new uplift above the high-water mark —
                uplift lost to a bad season is never re-charged on recovery.
                The fee line is illustrative only; no fee agreement is in
                effect and nothing is charged.
              </p>
            </Card>
          )}

          {/* this week, in plain words (owner-only; hidden on 403) */}
          {digestQ.data?.narrative && (
            <Card title="This week, in plain words" icon={Sparkles}
              right={digestQ.data.period_label && (
                <span className="text-xs text-gray-400">{digestQ.data.period_label}</span>
              )}>
              <p className="text-sm text-gray-700 whitespace-pre-line">{digestQ.data.narrative}</p>
              <Link to="/reports/weekly" className="inline-block mt-3 text-xs font-medium text-green-700 hover:text-green-900">
                Read the full Tuesday Report →
              </Link>
            </Card>
          )}

          {/* this week's checks — tap a tile to chart that driver */}
          {checks.length > 0 && (
            <Card title="This week's checks" icon={CheckCircle2}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {checks.map((c) => (
                  <button key={c.metric} type="button"
                    onClick={() => CHECK_METRIC[c.metric] && setMetric(CHECK_METRIC[c.metric])}
                    className={`rounded-lg border p-4 text-left ${CHECK_METRIC[c.metric] ? 'cursor-pointer hover:ring-2 hover:ring-green-200' : ''} ${c.status === 'behind' ? 'border-amber-300 bg-amber-50' : c.status === 'ahead' ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
                    title={CHECK_METRIC[c.metric] ? 'Show this driver on the chart' : undefined}>
                    <div className="text-sm font-medium text-gray-900 mb-1">{CHECK_LABELS[c.metric] || c.metric}</div>
                    <div className="text-lg font-bold">{Math.round(c.measured).toLocaleString()}</div>
                    <div className="text-xs text-gray-500">expected {Math.round(c.expected).toLocaleString()} ({Math.round(c.low).toLocaleString()}–{Math.round(c.high).toLocaleString()})</div>
                    <div className={`text-xs font-medium mt-1 ${c.status === 'behind' ? 'text-amber-700' : c.status === 'ahead' ? 'text-green-700' : 'text-gray-500'}`}>
                      {c.status === 'behind' ? 'Behind — being investigated' : c.status === 'ahead' ? 'Ahead of forecast' : 'On track'}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* grass vs herd (Phase 5) — only once land data measures capacity */}
          {grass && (
            <Card title="Grass vs herd" icon={Leaf}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Herd demand today</div>
                  <div className="text-lg font-bold">{Math.round(grass.demand).toLocaleString()} LSU</div>
                  <div className="text-xs text-gray-500 mt-1">one LSU ≈ a 450 kg animal's grazing</div>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Measured grass capacity</div>
                  <div className="text-lg font-bold">{Math.round(grass.capacity).toLocaleString()} LSU</div>
                  <div className="text-xs text-gray-500 mt-1">paddock areas × latest satellite/drone biomass</div>
                </div>
                <div className={`rounded-lg border p-4 ${grass.headroomPct != null && grass.headroomPct < 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Headroom</div>
                  <div className="text-lg font-bold">{grass.headroomPct == null ? '—' : `${Math.round(grass.headroomPct)}%`}</div>
                  <div className="text-xs text-gray-500 mt-1">{grass.headroomPct != null && grass.headroomPct < 0 ? 'stocked beyond measured grass — flagged, not hidden' : 'room before the grass runs out'}</div>
                </div>
              </div>
              {grass.bestGrassLimited && (
                <p className="text-xs text-gray-500 mt-3">
                  The best case is grass-limited: its herd growth is capped at
                  measured capacity (plus the pasture-recovery ramp), so more
                  grass — rest, biochar, restoration — is what unlocks the
                  next tier, not more breeding.
                </p>
              )}
              <button onClick={() => setMetric('herd_lsu')}
                className="mt-3 text-xs font-medium text-green-700 hover:text-green-900">
                Show grazing pressure on the chart →
              </button>
            </Card>
          )}

          {/* the three payments — the fee statement, in farmer units */}
          {fee && (
            <Card title="The three payments" icon={Receipt}
              right={<span className="text-xs text-gray-400">counted at the kraal — illustrative</span>}>
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                  Wage: UGX {Math.round(fee.terms.wage_ugx_head_month).toLocaleString()}/head/mo
                </span>
                <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700"
                      title={fee.terms.tier_note || undefined}>
                  Calf share: 1 in {fee.terms.calf_tier_one_in}
                  {fee.terms.measured_calving_rate != null
                    ? ` (calving ${Math.round(fee.terms.measured_calving_rate * 100)}%)`
                    : ' (calving being measured)'}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                  Better price: {Math.round(fee.terms.premium_share_frac * 100)}% above UGX {Math.round(fee.terms.reference_price_ugx_kg).toLocaleString()}/kg
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="text-left py-2 pr-3 font-semibold">Quarter</th>
                      <th className="text-right py-2 px-3 font-semibold">The Wage</th>
                      <th className="text-right py-2 px-3 font-semibold">The Calf Share</th>
                      <th className="text-right py-2 px-3 font-semibold">The Better Price</th>
                      <th className="text-right py-2 pl-3 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {fee.quarters.map((q) => (
                      <tr key={q.start} className="border-b border-gray-100 align-top">
                        <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{q.label}</td>
                        <td className="py-2 px-3 text-right">
                          <div className="text-gray-900">{fmtUGX(q.wage.amount_ugx)}</div>
                          <div className="text-xs text-gray-400">{q.wage.head.toLocaleString()} head × {q.wage.months} mo</div>
                        </td>
                        <td className="py-2 px-3 text-right" title={q.calf_share.note || undefined}>
                          <div className="text-gray-900">{q.calf_share.amount_ugx > 0 ? fmtUGX(q.calf_share.amount_ugx) : '—'}</div>
                          <div className="text-xs text-gray-400">
                            {q.calf_share.weaned_counted > 0
                              ? `${q.calf_share.weaned_counted} weaned → ${q.calf_share.calves_due_cum} due`
                              : `muster pending · ${q.calf_share.calvings_in_quarter} calvings`}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <div className="text-gray-900">{q.better_price.amount_ugx > 0 ? fmtUGX(q.better_price.amount_ugx) : '—'}</div>
                          <div className="text-xs text-gray-400">
                            {q.better_price.kg_priced > 0
                              ? `${q.better_price.kg_priced.toLocaleString()} kg priced`
                              : 'no priced channel sales'}
                            {q.better_price.heads_missing_price > 0
                              && ` · ${q.better_price.heads_missing_price} unpriced`}
                          </div>
                        </td>
                        <td className="py-2 pl-3 text-right font-bold text-gray-900">{fmtUGX(q.total_ugx)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {fee.ceiling && (
                <div className="mt-4 rounded-lg border border-green-200 bg-green-50/60 p-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-green-900 mb-1.5">
                    <span>The fairness ceiling — the twin watches us too</span>
                    <span>
                      {fee.ceiling.inside_ceiling == null
                        ? 'uplift accrues from the model-year start'
                        : fee.ceiling.inside_ceiling
                          ? 'inside the ceiling'
                          : 'above the ceiling — credited at the true-up'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex-1 bg-white border border-green-100 rounded h-3 overflow-hidden">
                      <div className="bg-green-700 h-3 rounded"
                           style={{ width: `${fee.ceiling.ceiling_ugx > 0 ? Math.min(100, fee.ceiling.fees_cumulative_ugx / fee.ceiling.ceiling_ugx * 100) : 100}%` }} />
                    </div>
                    <span className="tabular-nums text-gray-700 whitespace-nowrap">
                      paid {fmtUGX(fee.ceiling.fees_cumulative_ugx)} / ceiling {fee.ceiling.ceiling_ugx == null ? '—' : fmtUGX(fee.ceiling.ceiling_ugx)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">
                    Everything paid is capped at 20% of the measured uplift
                    above — any excess is credited back at the model-year
                    true-up. No fee agreement is in effect; nothing is charged.
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* the health-cost lever — the expense side of the twin */}
          {healthLever && (
            <Card title="The health-cost lever" icon={Stethoscope}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Today, per animal per year</div>
                  <div className="text-lg font-bold tabular-nums">UGX {Math.round(healthLever.base).toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-1">acaricides + treatment, from the farm's own books — tick control alone is UGX {Math.round(healthLever.baseA).toLocaleString()}</div>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50/60 p-4">
                  <div className="text-xs uppercase tracking-wide text-green-800 font-semibold mb-1">Achievable, per animal per year</div>
                  <div className="text-lg font-bold tabular-nums">UGX {Math.round(healthLever.best).toLocaleString()} <span className="text-sm font-semibold text-green-700">(−{healthLever.pct}%)</span></div>
                  <div className="text-xs text-gray-500 mt-1">45-day rest breaks the tick cycle; ECF immunization is added honestly before its savings arrive</div>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Why it matters</div>
                  <div className="text-sm text-gray-700 mt-1">Tick control is the farm's largest non-salary expense. Cutting it goes straight to the bottom line — and fewer chemicals on the animals is part of the quality story.</div>
                </div>
              </div>
              <button onClick={() => setMetric('annual_health_cost_ugx')}
                className="mt-3 text-xs font-medium text-green-700 hover:text-green-900">
                Show herd health spend on the chart →
              </button>
            </Card>
          )}

          {/* value waterfall (Phase 5) — owner-only finance lines */}
          {waterfall && (
            <Card title="Value created, honestly counted" icon={Scale}>
              {waterfall.gross_value_ugx > 0 && (
                <div className="space-y-1.5 mb-4">
                  {[
                    { label: 'Created', v: waterfall.gross_value_ugx, color: 'bg-green-600' },
                    { label: 'Costs', v: -(waterfall.direct_operating_costs?.total_ugx || 0), color: 'bg-amber-500' },
                    { label: 'Kept', v: waterfall.distributable_value_ugx, color: 'bg-green-800' },
                  ].map((b) => (
                    <div key={b.label} className="flex items-center gap-2 text-xs">
                      <span className="w-14 text-gray-500">{b.label}</span>
                      <div className="flex-1 bg-gray-100 rounded h-3 overflow-hidden">
                        <div className={`${b.color} h-3 rounded`}
                             style={{ width: `${Math.min(100, Math.abs(b.v) / waterfall.gross_value_ugx * 100)}%` }} />
                      </div>
                      <span className="w-28 text-right tabular-nums text-gray-700">{fmtUGX(b.v)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="divide-y divide-gray-100 text-sm">
                <div className="flex items-baseline justify-between py-2">
                  <span className="text-gray-700">Value created on the hoof <span className="text-gray-400">({Math.round(waterfall.liveweight_gap_kg).toLocaleString()} kg vs if nothing had changed, priced at the {waterfall.price?.basis === 'measured' ? 'measured market' : (waterfall.price?.basis || 'assumed')} price)</span></span>
                  <span className="font-bold text-gray-900">{fmtUGX(waterfall.gross_value_ugx)}</span>
                </div>
                <div className="flex items-baseline justify-between py-2">
                  <span className="text-gray-700">Recorded costs since the baseline <span className="text-gray-400">(every approved expense, unfiltered)</span></span>
                  <span className="font-semibold text-gray-900">− {fmtUGX(waterfall.direct_operating_costs?.total_ugx || 0)}</span>
                </div>
                <div className="flex items-baseline justify-between py-2">
                  <span className="text-gray-500">Assurance &amp; transaction costs · finance obligations <span className="text-gray-400">(not yet recorded separately)</span></span>
                  <span className="text-gray-500">− {fmtUGX(0)}</span>
                </div>
                <div className="flex items-baseline justify-between py-2">
                  <span className="font-semibold text-gray-900">Distributable additional value</span>
                  <span className={`font-bold ${waterfall.distributable_value_ugx >= 0 ? 'text-green-700' : 'text-amber-700'}`}>{fmtUGX(waterfall.distributable_value_ugx)}</span>
                </div>
              </div>
              {(waterfall.owner_context || []).length > 0 && (
                <div className="mt-3 text-xs text-gray-500">
                  Herd shares (context, not an allocation):{' '}
                  {waterfall.owner_context.map((o) => `${o.name} ${Math.round(o.herd_share * 100)}%`).join(' · ')}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                Display only — nothing is allocated or paid until an allocation
                rule set is adopted through governance. Settlement lives in
                Director Accounts.
              </p>
            </Card>
          )}

          {/* assumptions ledger */}
          <Card title="What this is built on" icon={BookOpenCheck}>
            <p className="text-sm text-gray-500 mb-4">
              Every number the twin uses, with how we know it. Nothing is
              hidden and nothing is edited — changes supersede, on the record.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[...liveByKey.entries()].map(([key, rows]) => {
                const base = rows.filter((r) => r.scenario === 'base' || r.scenario === 'shared')
                const best = rows.filter((r) => r.scenario === 'best')
                  .sort((a, b) => a.effective_from.localeCompare(b.effective_from))
                const badge = (base[0] || best[0])
                return (
                  <div key={key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm font-semibold text-gray-900">{KEY_LABELS[key] || key}</span>
                      {badge && <BasisBadge basis={badge.basis} />}
                    </div>
                    <div className="text-sm text-gray-700">
                      {base[0] && <span>Today: <b>{fmtAssumption(base[0].value, base[0].unit)}</b></span>}
                      {base[0] && best.length > 0 && <span className="text-gray-400"> · </span>}
                      {best.length > 0 && (
                        <span>
                          Achievable: <b>{best.map((r) => fmtAssumption(r.value, r.unit)).join(' → ')}</b>
                          {best.length > 1 && <span className="text-gray-500"> (phased to {new Date(best[best.length - 1].effective_from).getFullYear()})</span>}
                        </span>
                      )}
                    </div>
                    {(base[0] || best[0])?.source && (
                      <div className="text-xs text-gray-400 mt-1 truncate" title={(base[0] || best[0]).source}>
                        {(base[0] || best[0]).source}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>

          {/* what reality taught the model */}
          {(history.length > 0 || (statusQ.data?.exceptions || []).length > 0) && (
            <Card title="What reality taught the model" icon={History}>
              <ul className="space-y-2 text-sm">
                {(statusQ.data?.exceptions || []).map((e) => (
                  <li key={e.id} className="flex items-start gap-2">
                    {e.status === 'open' || e.status === 'investigating'
                      ? <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      : <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />}
                    <span>
                      <b>{e.title}</b>
                      {e.metric_current != null && e.metric_expected != null && (
                        <span className="text-gray-500"> — measured {Math.round(e.metric_current).toLocaleString()} vs expected {Math.round(e.metric_expected).toLocaleString()}</span>
                      )}
                      <span className="text-gray-400"> · {new Date(e.detected_at).toLocaleDateString()}{e.resolved_at ? ' · cleared' : ''}</span>
                    </span>
                  </li>
                ))}
                {history.slice(0, 8).map((r) => (
                  <li key={r.id} className="flex items-start gap-2">
                    <History className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    <span>
                      <b>{KEY_LABELS[r.key] || r.key}</b> ({r.scenario}) {fmtAssumption(r.value, r.unit)} was superseded
                      <span className="text-gray-400"> · {new Date(r.superseded_at).toLocaleDateString()}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
